// URL Parameter Sync Utilities for Media Library Filters
import type { MediaFilters } from "shared/types/files";
import { PAGES } from "constants/MediaLibrary";

// Page to URL tab mapping
export const PAGE_TO_TAB: Record<string, string> = {
  [PAGES.COLLECTIONS]: "collections",
  [PAGES.STOCKS]: "stocks",
  [PAGES.CHROME]: "chrome",
  [PAGES.ZAPIER]: "zapier",
  [PAGES.TAGGED_IN]: "tagged-in",
  [PAGES.HASHTAGS]: "hashtags",
  [PAGES.CREATOR]: "creator",
};

// URL tab to page mapping
export const TAB_TO_PAGE: Record<string, string> = {
  collections: PAGES.COLLECTIONS,
  stocks: PAGES.STOCKS,
  chrome: PAGES.CHROME,
  zapier: PAGES.ZAPIER,
  "tagged-in": PAGES.TAGGED_IN,
  hashtags: PAGES.HASHTAGS,
  creator: PAGES.CREATOR,
};

export interface LibraryUrlState {
  tab?: string;
  account?: string;
  hashtag?: string;
  creator?: string;
}

/**
 * Parses library URL state from search params
 */
export const parseLibraryUrlState = (
  params: URLSearchParams
): LibraryUrlState => {
  return {
    tab: params.get("tab") || undefined,
    account: params.get("account") || undefined,
    hashtag: params.get("hashtag") || undefined,
    creator: params.get("creator") || undefined,
  };
};

/**
 * Updates library URL parameters
 * Cleans up params not relevant to current tab
 */
export const updateLibraryUrl = (
  currentParams: URLSearchParams,
  updates: Partial<LibraryUrlState>
): URLSearchParams => {
  const newParams = new URLSearchParams(currentParams);

  if (updates.tab !== undefined) newParams.set("tab", updates.tab);
  if (updates.account !== undefined) newParams.set("account", updates.account);
  if (updates.hashtag !== undefined) newParams.set("hashtag", updates.hashtag);
  if (updates.creator !== undefined) newParams.set("creator", updates.creator);

  // Clean up params not relevant to current tab
  const tab = updates.tab || newParams.get("tab");
  if (tab !== "hashtags") newParams.delete("hashtag");
  if (tab !== "creator") newParams.delete("creator");
  if (!["tagged-in", "hashtags", "creator"].includes(tab || "")) {
    newParams.delete("account");
  }

  // Clean up collection param when switching away from collections
  if (tab && tab !== "collections") {
    newParams.delete("collection");
  }

  return newParams;
};

/**
 * Serializes filter object to URLSearchParams
 * Only includes non-empty/non-default values to keep URLs clean
 */
export const serializeFiltersToURL = (
  filters: Partial<MediaFilters>
): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.name) params.set("name", filters.name);
  if (filters.description) params.set("description", filters.description);
  if (filters.sourceName)
    params.set(
      "sourceName",
      typeof filters.sourceName === "string"
        ? filters.sourceName
        : String(filters.sourceName)
    );
  if (filters.tags && filters.tags.length > 0)
    params.set("tags", filters.tags.join(","));
  if (filters.favorite) params.set("favorite", "true");
  if (filters.mediaType) params.set("mediaType", filters.mediaType);
  if (filters.usage) params.set("usage", filters.usage);
  if (filters.sort && filters.sort !== "-created")
    params.set("sort", String(filters.sort));

  return params;
};

/**
 * Parses URLSearchParams to filter object
 * Returns partial object with only values present in URL
 */
export const parseFiltersFromURL = (
  searchParams: URLSearchParams
): Partial<MediaFilters> => {
  const filters: Partial<MediaFilters> = {};

  const name = searchParams.get("name");
  if (name) filters.name = name;

  const description = searchParams.get("description");
  if (description) filters.description = description;

  const sourceName = searchParams.get("sourceName");
  if (sourceName) filters.sourceName = sourceName as any; // URL params are strings, will be validated by Redux

  const tags = searchParams.get("tags");
  if (tags) filters.tags = tags.split(",").filter(Boolean);

  const favorite = searchParams.get("favorite");
  if (favorite === "true") filters.favorite = true;

  const mediaType = searchParams.get("mediaType");
  if (mediaType && (mediaType === "image" || mediaType === "video")) {
    filters.mediaType = mediaType;
  }

  const usage = searchParams.get("usage");
  if (usage && (usage === "used" || usage === "unused")) {
    filters.usage = usage;
  }

  const sort = searchParams.get("sort");
  if (sort) filters.sort = sort as any; // URL params are strings, will be validated by Redux

  return filters;
};

/**
 * Serializes pagination to URLSearchParams
 * Only includes non-default values (page > 1, pageSize != 20)
 */
export const serializePaginationToURL = (
  page: number,
  pageSize: number
): URLSearchParams => {
  const params = new URLSearchParams();
  if (page > 1) params.set("page", String(page));
  if (pageSize !== 20) params.set("pageSize", String(pageSize));
  return params;
};

/**
 * Parses pagination from URLSearchParams
 * Returns default values if not present in URL
 */
export const parsePaginationFromURL = (searchParams: URLSearchParams) => {
  return {
    page: parseInt(searchParams.get("page") || "1", 10),
    pageSize: parseInt(searchParams.get("pageSize") || "20", 10),
  };
};
